/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
//module Shumway.AVM1.Lib {
import { jsGlobal, notImplemented, release, Debug, somewhatImplemented, warning } from '@awayfl/swf-loader';
import { avm1BroadcastEvent, DEPTH_OFFSET, getAVM1Object, wrapAVM1NativeMembers } from './AVM1Utils';
import { alCoerceNumber, alCoerceString, alIsFunction, alNewObject, alToInt32, alToInteger, alToNumber, alToString } from '../runtime';
import { AVM1MovieClip } from './AVM1MovieClip';
import { AVM1Broadcaster } from './AVM1Broadcaster';
import { AVM1System } from './AVM1System';
import { AVM1Stage } from './AVM1Stage';
import { AVM1Button } from './AVM1Button';
import { AVM1TextField } from './AVM1TextField';
import { AVM1Color } from './AVM1Color';
import { AVM1Key } from './AVM1Key';
import { AVM1Mouse } from './AVM1Mouse';
import { AVM1MovieClipLoader } from './AVM1MovieClipLoader';
import { AVM1LoadVarsFunction } from './AVM1LoadVars';
import { AVM1Sound } from './AVM1Sound';
import { AVM1SharedObject } from './AVM1SharedObject';
import { AVM1TextFormat } from './AVM1TextFormat';
import { AVM1XMLFunction, AVM1XMLNodeFunction } from './AVM1XML';
import { AVM1BitmapData } from './AVM1BitmapData';
import { AVM1PointFunction } from './AVM1Point';
import { AVM1RectangleFunction } from './AVM1Rectangle';
import { AVM1Transform } from './AVM1Transform';
import { AVM1ColorTransformFunction } from './AVM1ColorTransform';
import { AVM1ExternalInterface } from './AVM1ExternalInterface';
import { createFiltersClasses } from './AVM1Filters';
import { URLLoaderEvent, AudioManager, URLRequest, URLLoader, URLLoaderDataFormat } from '@awayjs/core';
import { MovieClip, FrameScriptManager } from '@awayjs/scene';
import { create } from 'random-seed';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Selection } from './AVM1Selection';
import { AVM1Function } from '../runtime/AVM1Function';
import { AVM1ArrayNative } from '../natives';
import { AVM1MatrixFunction } from './AVM1Matrix';
var _escape = jsGlobal.escape;
var _internalTimeouts = [];
var TraceLevel = /** @class */ (function () {
    function TraceLevel() {
    }
    TraceLevel.ALL = 'all';
    TraceLevel.NONE = 'none';
    TraceLevel.IMPORTANT = 'important';
    return TraceLevel;
}());
export { TraceLevel };
var AVM1Globals = /** @class */ (function (_super) {
    __extends(AVM1Globals, _super);
    function AVM1Globals(context) {
        var _this = _super.call(this, context) || this;
        // once assigned from AVM1Handler
        _this.SWF_BASE_URL = '';
        _this.registeredLevels = {};
        // built-ins
        _this.NaN = Number.NaN;
        _this.Infinity = Number.POSITIVE_INFINITY;
        _this.newline = '\n';
        _this.undefined = undefined;
        _this._initBuiltins(context);
        _this._initializeFlashObject(context);
        _this.buttonCheckFunc = new AVM1Function(context);
        _this.buttonCheckFunc.alCall = function (_thisArg, _args) {
            return AVM1Mouse.mouseButtonsState[_args[0]];
        };
        return _this;
    }
    AVM1Globals.generateRandomSeed = function () {
        var str = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        var len = 10 + Math.round(Math.random() * 10);
        var seed = '';
        for (var i = 0; i < len; i++) {
            seed += str[Math.floor(Math.random() * str.length)];
        }
        return seed;
    };
    AVM1Globals.setRandom = function (seed) {
        if (seed === void 0) { seed = null; }
        if (seed) {
            if (seed == AVM1Globals.GENERATE_SEED) {
                seed = AVM1Globals.generateRandomSeed();
            }
            AVM1Globals.randomProvider = create(seed);
            return seed;
        }
        AVM1Globals.randomProvider = null;
        return null;
    };
    AVM1Globals.registerCustomClass = function (name, avm1Class) {
        AVM1Globals._registeredCustomClasses[name] = avm1Class;
        if (AVM1Globals.instance) {
            AVM1Globals.instance.alPut(name, avm1Class);
        }
    };
    AVM1Globals.registerCustomClassInstance = function (name, avm1Class) {
        AVM1Globals._registeredCustomClassInstances[name] = avm1Class;
        if (AVM1Globals.instance) {
            AVM1Globals.instance.alPut(name, avm1Class);
        }
    };
    AVM1Globals.createGlobalsObject = function (context) {
        var globals = new AVM1Globals(context);
        wrapAVM1NativeMembers(context, globals, globals, ['flash', 'ASnative', 'ASSetPropFlags', 'BitmapData', 'clearInterval', 'clearTimeout', 'ExternalInterface',
            'escape', 'unescape', 'setInterval', 'setTimeout', 'showRedrawRegions',
            'trace', 'updateAfterEvent', 'myName',
            'NaN', 'Infinity', 'isFinite', 'isNaN', 'parseFloat', 'parseInt', 'undefined',
            'Object', 'Function', 'Array', 'Number', 'Math', 'Boolean', 'Date', 'Selection', 'String', 'Error',
            'MovieClip', 'AsBroadcaster', 'System', 'Stage', 'Button',
            'TextField', 'Color', 'Key', 'Mouse', 'MovieClipLoader', 'newline', 'XML', 'XMLNode', 'LoadVars',
            'Sound', 'SharedObject', 'ContextMenu', 'ContextMenuItem', 'TextFormat'], false);
        //'myManager', 'SoundManager'], false);
        AVM1Globals.instance = globals;
        return globals;
    };
    AVM1Globals.prototype._getLevelForRoot = function (root) {
        for (var key in this.registeredLevels) {
            if (this.registeredLevels[key] === root) {
                return +key;
            }
        }
        return -1;
    };
    AVM1Globals.prototype._getRootForLevel = function (level) {
        return this.registeredLevels[level];
    };
    AVM1Globals.prototype._addRoot = function (level, root) {
        if (this.registeredLevels[level])
            return this.registeredLevels[level];
        getAVM1Object(root, this.context).adaptee;
        AVM1Stage.avmStage.root.addChildAt(root, level);
        if (root.adapter != root) {
            root.adapter.initAdapter();
        }
        this.registeredLevels[level] = root;
        return root;
    };
    AVM1Globals.prototype._removeRoot = function (level) {
        if (this.registeredLevels[level]) {
            AVM1Stage.avmStage.root.removeChild(this.registeredLevels[level]);
            delete this.registeredLevels[level];
        }
    };
    AVM1Globals.prototype.ASnative = function (classID, id) {
        // more interesting about this
        // eslint-disable-next-line max-len
        // http://etutorials.org/Macromedia/Flash+hacks.+100+industrial-strength+tips+tools/Chapter+8.+User+Interface+Elements/Hack+62+Right+and+Middle+Mouse+Buttons/
        if (classID === 800 && id === 2) {
            return this.buttonCheckFunc;
        }
        console.log('ASnatives', classID, id);
        return null;
    };
    AVM1Globals.prototype.ASSetPropFlags = function (_obj, _children, _flags, _allowFalse) {
        // flags (from bit 0): dontenum, dontdelete, readonly, ....
        // TODO
    };
    AVM1Globals.prototype.clearIntervals = function () {
        var i = 0;
        var len = _internalTimeouts.length;
        for (i = 0; i < len; i++) {
            FrameScriptManager.clearInterval(_internalTimeouts[i]);
        }
        _internalTimeouts.length = 0;
    };
    AVM1Globals.prototype.clearInterval = function (id /* uint */) {
        var internalId = _internalTimeouts[id - 1];
        if (internalId) {
            FrameScriptManager.clearInterval(internalId);
            delete _internalTimeouts[id - 1];
        }
    };
    AVM1Globals.prototype.clearTimeout = function (id /* uint */) {
        var internalId = _internalTimeouts[id - 1];
        if (internalId) {
            FrameScriptManager.clearTimeout(internalId);
            delete _internalTimeouts[id - 1];
        }
    };
    /**
         * AVM1 escapes slightly more characters than JS's encodeURIComponent, and even more than
         * the deprecated JS version of escape. That leaves no other option but to do manual post-
         * processing of the encoded result. :/
         *
         * Luckily, unescape isn't thus afflicted - it happily unescapes all the additional things
         * we escape here.
         */
    AVM1Globals.prototype.escape = function (str) {
        var result = encodeURIComponent(str);
        return result.replace(/!|'|\(|\)|\*|-|\.|_|~/g, function (char) {
            switch (char) {
                case '*':
                    return '%2A';
                case '-':
                    return '%2D';
                case '.':
                    return '%2E';
                case '_':
                    return '%5F';
                default:
                    return _escape(char);
            }
        });
    };
    AVM1Globals.prototype.unescape = function (str) {
        return decodeURIComponent(str);
    };
    AVM1Globals.prototype.setInterval = function () {
        // AVM1 setInterval silently swallows everything that vaguely looks like an error.
        if (arguments.length < 2) {
            return undefined;
        }
        // eslint-disable-next-line prefer-rest-params
        var inputArgs = arguments;
        var context = this.context;
        var fn;
        var time = undefined;
        var argsStartIdx = 2;
        if (alIsFunction(inputArgs[0])) {
            fn = inputArgs[0].toJSFunction();
            time = inputArgs[1];
        }
        else {
            if (inputArgs.length < 3) {
                return undefined;
            }
            var obj_1 = inputArgs[0];
            var funName_1 = inputArgs[1];
            time = inputArgs[2];
            argsStartIdx = 3;
            if (!(obj_1 instanceof AVM1Object) || typeof funName_1 !== 'string') {
                return undefined;
            }
            fn = function () {
                var avmFn = obj_1.alGet(funName_1);
                if (!alIsFunction(avmFn)) {
                    return;
                }
                // internal arguments
                var args = Array.prototype.slice.call(inputArgs, 0);
                context.executeFunction(avmFn, obj_1, args);
            };
        }
        // AS2 skip setInterval when interval is invalid
        if (time === undefined) {
            return undefined;
        }
        // Invalid cast. Return 0 for NaN/undefined.
        time = alToInteger(context, time);
        var args = Array.prototype.slice.call(inputArgs, argsStartIdx);
        // Unconditionally coerce interval to int, as one would do.
        var internalId;
        if (fn) {
            var callback = function () {
                // eslint-disable-next-line prefer-spread
                fn.apply(null, args);
            };
            // or interval manager must skip invalid intervals and return undef instead number
            internalId = FrameScriptManager.setInterval(callback, time);
        }
        return _internalTimeouts.push(internalId);
    };
    AVM1Globals.prototype.setTimeout = function () {
        // eslint-disable-next-line prefer-rest-params
        var inputArgs = arguments;
        // AVM1 setTimeout silently swallows most things that vaguely look like errors.
        if (inputArgs.length < 2 || !alIsFunction(inputArgs[0])) {
            return undefined;
        }
        var fn = inputArgs[0].toJSFunction();
        var time = alToInteger(this.context, inputArgs[1]);
        var args = [];
        var argsLength = inputArgs.length;
        for (var i = 2; i < argsLength; i++) {
            args.push(inputArgs[i]);
        }
        var callback = function () {
            // eslint-disable-next-line prefer-spread
            fn.apply(null, args);
        };
        var internalId = FrameScriptManager.setTimeOut(callback, time);
        return _internalTimeouts.push(internalId);
    };
    AVM1Globals.prototype.showRedrawRegions = function (_enable, _color) {
        // flash.profiler.showRedrawRegions.apply(null, arguments);
        notImplemented('AVM1Globals.showRedrawRegions');
    };
    AVM1Globals.prototype.trace = function (expression) {
        if (this)
            this.context.actions.trace(expression);
    };
    AVM1Globals.prototype.updateAfterEvent = function () {
        notImplemented('AVM1Globals.updateAfterEvent');
        //this.context.sec.player.requestRendering();
    };
    AVM1Globals.prototype.isFinite = function (n) {
        return isFinite(alToNumber(this.context, n));
    };
    AVM1Globals.prototype.isNaN = function (n) {
        if (n === ' ') {
            return true;
        }
        return isNaN(alToNumber(this.context, n));
    };
    AVM1Globals.prototype.parseFloat = function (s) {
        return parseFloat(alToString(this.context, s));
    };
    AVM1Globals.prototype.parseInt = function (s, radix) {
        return parseInt(alToString(this.context, s), alToInt32(this.context, radix));
    };
    AVM1Globals.prototype._initBuiltins = function (context) {
        var builtins = context.builtins;
        this.Object = builtins.Object;
        this.Function = builtins.Function;
        this.Array = builtins.Array;
        this.Number = builtins.Number;
        this.Math = builtins.Math;
        this.Boolean = builtins.Boolean;
        this.Date = builtins.Date;
        this.String = builtins.String;
        this.Error = builtins.Error;
        this.Selection = AVM1Selection.createAVM1Class(context)._ownProperties['prototype'].value;
        this.ExternalInterface = AVM1ExternalInterface.createAVM1Class(context);
        this.MovieClip = AVM1MovieClip.createAVM1Class(context);
        this.AsBroadcaster = AVM1Broadcaster.createAVM1Class(context);
        this.System = AVM1System.createAVM1Class(context);
        this.Stage = AVM1Stage.createAVM1Class(context);
        this.Button = AVM1Button.createAVM1Class(context);
        this.TextField = AVM1TextField.createAVM1Class(context);
        this.Color = AVM1Color.createAVM1Class(context);
        this.Key = AVM1Key.createAVM1Class(context);
        this.Mouse = AVM1Mouse.createAVM1Class(context);
        this.MovieClipLoader = AVM1MovieClipLoader.createAVM1Class(context);
        this.LoadVars = new AVM1LoadVarsFunction(context);
        this.Sound = AVM1Sound.createAVM1Class(context);
        this.SharedObject = AVM1SharedObject.createAVM1Class(context);
        this.ContextMenu = undefined; // wrapAVM1Builtin(sec.flash.ui.ContextMenu.axClass);
        this.ContextMenuItem = undefined; // wrapAVM1Builtin(sec.flash.ui.ContextMenuItem.axClass);
        this.TextFormat = AVM1TextFormat.createAVM1Class(context);
        this.XMLNode = new AVM1XMLNodeFunction(context);
        this.XML = new AVM1XMLFunction(context, this.XMLNode);
        this.BitmapData = AVM1BitmapData.createAVM1Class(context);
        this.Matrix = new AVM1MatrixFunction(context);
        this.Point = new AVM1PointFunction(context);
        this.Rectangle = new AVM1RectangleFunction(context);
        this.Transform = AVM1Transform.createAVM1Class(context);
        this.ColorTransform = new AVM1ColorTransformFunction(context);
        AVM1Broadcaster.initialize(context, this.Stage);
        AVM1Broadcaster.initialize(context, this.Key);
        AVM1Broadcaster.initialize(context, this.Mouse);
        // register own custom classes and class identifier
        for (var k in AVM1Globals._registeredCustomClasses) {
            this.alPut(k, AVM1Globals._registeredCustomClasses[k]);
        }
        for (var k in AVM1Globals._registeredCustomClassInstances) {
            this.alPut(k, AVM1Globals._registeredCustomClassInstances[k]);
        }
    };
    AVM1Globals.prototype._initializeFlashObject = function (context) {
        this.flash = alNewObject(context);
        var display = alNewObject(context);
        display.alPut('BitmapData', this.BitmapData);
        this.flash.alPut('display', display);
        var external = alNewObject(context);
        external.alPut('ExternalInterface', AVM1ExternalInterface.createAVM1Class(context));
        this.flash.alPut('external', external);
        var filters = createFiltersClasses(context);
        this.flash.alPut('filters', filters);
        this.filters = filters;
        var geom = alNewObject(context);
        geom.alPut('ColorTransform', this.ColorTransform);
        geom.alPut('Matrix', this.Matrix);
        geom.alPut('Point', this.Point);
        geom.alPut('Rectangle', this.Rectangle);
        geom.alPut('Transform', this.Transform);
        this.flash.alPut('geom', geom);
        var text = alNewObject(context);
        this.flash.alPut('text', text);
    };
    AVM1Globals.swfStartTime = Date.now();
    AVM1Globals._registeredCustomClasses = {};
    AVM1Globals._registeredCustomClassInstances = {};
    AVM1Globals.GENERATE_SEED = 'GENERATE_SEED';
    AVM1Globals.tracelevel = TraceLevel.NONE;
    AVM1Globals.toStringPrecision = -1;
    return AVM1Globals;
}(AVM1Object));
export { AVM1Globals };
var AVM1NativeActions = /** @class */ (function () {
    function AVM1NativeActions(context) {
        this.context = context;
        // TODO ?
    }
    AVM1NativeActions.prototype.asfunction = function (link) {
        notImplemented('AVM1NativeActions.$asfunction');
    };
    AVM1NativeActions.prototype.call = function (frame) {
        // 	calls a framescript on a given frame (label or frame-number)
        //	without navigating the mc to that frame
        var nativeTarget = this.context.resolveTarget(null);
        nativeTarget._callFrame(frame);
    };
    AVM1NativeActions.prototype.chr = function (code) {
        // return the string for a given charcode
        code = alToInteger(this.context, code);
        if (this.context.swfVersion <= 5) {
            code &= 0xFF;
        }
        return code ? String.fromCharCode(code) : '';
    };
    AVM1NativeActions.prototype.duplicateMovieClip = function (target, newname, depth) {
        // todo: check if the DEPTH_OFFSET is 100% correct for working with AwayJS
        var normalizedDepth = alCoerceNumber(this.context, depth) - DEPTH_OFFSET;
        var nativeTarget = this.context.resolveTarget(target);
        nativeTarget.duplicateMovieClip(newname, normalizedDepth, null);
    };
    AVM1NativeActions.prototype.fscommand = function (command, args) {
        // todo: untested
        somewhatImplemented('AVM1NativeActions.fscommand');
        // return this.context.sec.flash.system.fscommand.axCall(null, this.context.sec, command, args);
    };
    AVM1NativeActions.prototype.getTimer = function () {
        return Date.now() - AVM1Globals.swfStartTime;
    };
    AVM1NativeActions.prototype.getURL = function (url, target, method) {
        url = String(url);
        if (typeof target === 'string' && target.indexOf('_level') === 0) {
            this.loadMovieNum(url, +target.substr(6), method);
            return;
        }
        if (url.toLowerCase().indexOf('fscommand:') === 0) {
            console.log('fsCommand not implemented ');
            return;
        }
        window.open(url, target);
    };
    AVM1NativeActions.prototype.gotoAndPlay = function (label, frame) {
        //console.log("AVM1Globals.gotoAndPlay", label, frame);
        // even if gotoAndPlay fails to find a label, the mc wills till be set to play
        // this is diffeent when calling gotoAndPlaycalling on mc directly)
        var avmMC = this.context.resolveTarget(null);
        avmMC.adaptee.play();
        this._gotoFrame(avmMC.adaptee, label, frame);
    };
    AVM1NativeActions.prototype.gotoAndStop = function (label, frame) {
        // see comments in gotoAndPlay
        //console.log("AVM1Globals.gotoAndStop", label, frame);
        //console.log("Scene navigation", arguments[0], scene);
        var avmMC = this.context.resolveTarget(null);
        if (typeof frame === 'number' && frame <= 0)
            return;
        avmMC.adaptee.stop();
        this._gotoFrame(avmMC.adaptee, label, frame);
    };
    AVM1NativeActions.prototype._gotoFrame = function (mc, frame, offset) {
        if (Array.isArray(frame)) {
            if (frame.length == 0)
                return;
            frame = frame[0];
        }
        if (frame instanceof AVM1ArrayNative) {
            if (!frame.value || frame.value.length == 0)
                return;
            frame = frame.value[0];
        }
        if (typeof frame === 'number') {
            if (frame % 1 !== 0) {
                frame = frame.toString();
            }
        }
        if (typeof frame === 'string') {
            var labelName = frame.toLowerCase();
            if (mc.timeline._labels[labelName] === null) {
                frame = parseInt(frame);
                if (!isNaN(frame)) {
                    mc.currentFrameIndex = frame - 1;
                }
                return;
            }
            mc.jumpToLabel(labelName, offset);
        }
        else
            mc.currentFrameIndex = frame - 1;
    };
    AVM1NativeActions.prototype.ifFrameLoaded = function (scene, frame) {
        // ignoring scene parameter ?
        var nativeTarget = this.context.resolveTarget(null);
        // eslint-disable-next-line prefer-rest-params
        var frameNum = arguments.length < 2 ? arguments[0] : arguments[1];
        var framesLoaded = nativeTarget.alGet('_framesloaded');
        var totalFrames = nativeTarget.alGet('_totalframes');
        // The (0-based) requested frame index is clamped to (the 1-based) totalFrames value.
        // I.e., asking if frame 20 is loaded in a timline with only 10 frames returns true if all
        // frames have been loaded.
        return Math.min(frameNum + 1, totalFrames) <= framesLoaded;
    };
    AVM1NativeActions.prototype.length_ = function (expression) {
        return ('' + expression).length; // ASCII Only?
    };
    AVM1NativeActions.prototype.loadMovie = function (url, target, method) {
        // some swfs are using loadMovie to call fscommmand
        if (url && url.toLowerCase().indexOf('fscommand:') === 0) {
            this.fscommand(url.substring('fscommand:'.length), target);
            return;
        }
        if (url === '' && target) {
            target.unloadMovie();
            return;
        }
        // make all relative urls raltive to first loaded game-swf:
        if (url != '' && url.indexOf('http://') !== 0 && url.indexOf('https://') !== 0) {
            url = this.context.globals.SWF_BASE_URL + url;
        }
        var loadLevel = typeof target === 'string' && target.indexOf('_level') === 0;
        var levelNumber;
        if (loadLevel) {
            var levelStr = target.substr(6);
            levelNumber = parseInt(levelStr, 10);
            loadLevel = levelNumber.toString() === levelStr;
        }
        if (loadLevel) {
            this.loadMovieNum(url, levelNumber, method);
        }
        else {
            var nativeTarget = this.context.resolveTarget(target);
            nativeTarget.loadMovie(url, method);
        }
    };
    AVM1NativeActions.prototype.loadMovieNum = function (url, level, method) {
        //console.log("AVM1NativeActions.loadMovieNum:", url, level, method);
        url = alCoerceString(this.context, url);
        level = alToInteger(this.context, level);
        method = alCoerceString(this.context, method);
        // some swfs are using loadMovieNum to call fscommmand
        if (url && url.toLowerCase().indexOf('fscommand:') === 0) {
            return this.fscommand(url.substring('fscommand:'.length));
        }
        if (url == '') {
            this.context.globals._removeRoot(level);
            return;
        }
        var newLevel = this.context.globals._addRoot(level, new MovieClip());
        // make all relative urls raltive to first loaded game-swf:
        if (url.indexOf('http://') !== 0 && url.indexOf('https://') !== 0) {
            url = this.context.globals.SWF_BASE_URL + url;
        }
        newLevel.adapter.loadMovie(url, method);
    };
    AVM1NativeActions.prototype.loadVariables = function (url, target, method) {
        if (method === void 0) { method = ''; }
        url = alCoerceString(this.context, url);
        method = alCoerceString(this.context, method);
        var nativeTarget = this.context.resolveTarget(target);
        if (!nativeTarget) {
            return; // target was not found
        }
        this._loadVariables(nativeTarget, url, method);
    };
    AVM1NativeActions.prototype.loadVariablesNum = function (url, level, method) {
        if (method === void 0) { method = ''; }
        url = alCoerceString(this.context, url);
        level = alToInteger(this.context, level);
        method = alCoerceString(this.context, method);
        var nativeTarget = this.context.resolveLevel(level);
        if (!nativeTarget) {
            return; // target was not found
        }
        this._loadVariables(nativeTarget, url, method);
    };
    AVM1NativeActions.prototype._loadVariables = function (nativeTarget, url, method) {
        var context = this.context;
        var request = new URLRequest(url);
        if (method) {
            request.method = method;
        }
        var loader = new URLLoader();
        //loader._ignoreDecodeErrors = true;
        loader.dataFormat = 'variables'; // flash.net.URLLoaderDataFormat.VARIABLES;
        var completeHandler = function (event) {
            loader.removeEventListener(URLLoaderEvent.LOAD_COMPLETE, completeHandler);
            // If the response data is empty, URLLoader#data contains an empty string.
            if (loader.dataFormat == URLLoaderDataFormat.VARIABLES) {
                release || Debug.assert(typeof loader.data === 'object');
                for (var key in loader.data)
                    context.utils.setProperty(nativeTarget, key, loader.data[key]);
            }
            if (nativeTarget instanceof AVM1MovieClip) {
                avm1BroadcastEvent(context, nativeTarget, 'onData');
            }
        };
        loader.addEventListener(URLLoaderEvent.LOAD_COMPLETE, completeHandler);
        loader.load(request);
    };
    AVM1NativeActions.prototype.mbchr = function (code) {
        code = alToInteger(this.context, code);
        return code ? String.fromCharCode(code) : '';
    };
    AVM1NativeActions.prototype.mblength = function (expression) {
        return ('' + expression).length;
    };
    AVM1NativeActions.prototype.mbord = function (character) {
        return ('' + character).charCodeAt(0);
    };
    AVM1NativeActions.prototype.mbsubstring = function (value, index, count) {
        if (index !== (0 | index) || count !== (0 | count)) {
            // index or count are not integers, the result is the empty string.
            return '';
        }
        index = (index < 1) ? 0 : index - 1;
        if (count < 0)
            count = undefined;
        return ('' + value).substr(index, count);
    };
    AVM1NativeActions.prototype.nextFrame = function () {
        var nativeTarget = this.context.resolveTarget(null);
        var awayObject = nativeTarget.adaptee;
        awayObject.stop();
        ++awayObject.currentFrameIndex;
    };
    AVM1NativeActions.prototype.nextScene = function () {
        notImplemented('AVM1NativeActions.nextScene');
        //var nativeTarget:AVM1MovieClip = <AVM1MovieClip>this.context.resolveTarget(null);
        //var awayObject:MovieClip = <MovieClip>getAwayJSAdaptee(nativeTarget);
    };
    AVM1NativeActions.prototype.ord = function (character) {
        return ('' + character).charCodeAt(0); // ASCII only?
    };
    AVM1NativeActions.prototype.play = function () {
        this.context.resolveTarget(null).play();
    };
    AVM1NativeActions.prototype.prevFrame = function () {
        var nativeTarget = this.context.resolveTarget(null);
        var awayObject = nativeTarget.adaptee;
        --awayObject.currentFrameIndex;
        awayObject.stop();
    };
    AVM1NativeActions.prototype.prevScene = function () {
        notImplemented('AVM1NativeActions.prevScene');
    };
    AVM1NativeActions.prototype.print = function (target, boundingBox) {
        notImplemented('AVM1NativeActions.print');
    };
    AVM1NativeActions.prototype.printAsBitmap = function (target, boundingBox) {
        notImplemented('AVM1NativeActions.printAsBitmap');
    };
    AVM1NativeActions.prototype.printAsBitmapNum = function (level, boundingBox) {
        notImplemented('AVM1NativeActions.printAsBitmapNum');
    };
    AVM1NativeActions.prototype.printNum = function (level, bondingBox) {
        notImplemented('AVM1NativeActions.printNum');
    };
    AVM1NativeActions.prototype.random = function (value) {
        if (AVM1Globals.randomProvider) {
            return 0 | (AVM1Globals.randomProvider.random() * (0 | value));
        }
        return 0 | (Math.random() * (0 | value));
    };
    AVM1NativeActions.prototype.removeMovieClip = function (target) {
        if (!target) {
            warning('AVM1NativeActions.removeMovieClip - called for undefined');
            return;
        }
        var nativeTarget = this.context.resolveTarget(target);
        if (!nativeTarget) {
            warning('AVM1NativeActions.removeMovieClip - target not found');
            return;
        }
        nativeTarget.removeMovieClip();
    };
    AVM1NativeActions.prototype.startDrag = function (target) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var mc = this.context.resolveTarget(target);
        if (mc) {
            // eslint-disable-next-line prefer-spread
            mc.startDrag.apply(mc, args);
        }
    };
    AVM1NativeActions.prototype.stop = function () {
        var nativeTarget = this.context.resolveTarget(null);
        nativeTarget.stop();
    };
    AVM1NativeActions.prototype._stopSoundsOnObjectsRecursivly = function (obj) {
        if (!obj.numChildren)
            return;
        for (var i = 0; i < obj.numChildren; i++) {
            if (obj.getChildAt(i).isAsset(MovieClip)) {
                obj.getChildAt(i).stopSounds();
            }
            else
                this._stopSoundsOnObjectsRecursivly(obj.getChildAt(i));
        }
    };
    AVM1NativeActions.prototype.stopAllSounds = function () {
        AudioManager.stopAllSounds();
        this._stopSoundsOnObjectsRecursivly(AVM1Stage.avmStage.root);
    };
    AVM1NativeActions.prototype.stopDrag = function () {
        if (AVM1MovieClip.currentDraggedMC) {
            AVM1MovieClip.currentDraggedMC.stopDrag();
        }
    };
    AVM1NativeActions.prototype.substring = function (value, index, count) {
        return this.mbsubstring(value, index, count); // ASCII Only?
    };
    AVM1NativeActions.prototype.toggleHighQuality = function () {
        // flash.display.Stage.quality
        notImplemented('AVM1NativeActions.toggleHighQuality');
    };
    AVM1NativeActions.prototype.trace = function (expression) {
        if (AVM1Globals.tracelevel == TraceLevel.NONE)
            return;
        var value;
        var isImportantTrace = false;
        switch (typeof expression) {
            case 'undefined':
                // undefined is always 'undefined' for trace (even for SWF6).
                value = 'undefined';
                break;
            case 'string':
                value = expression;
                isImportantTrace = value.length > 0 && value[0] == '!';
                break;
            default:
                value = alToString(this.context, expression);
                break;
        }
        if (AVM1Globals.tracelevel == TraceLevel.IMPORTANT && !isImportantTrace)
            return;
        console.log('%cAVM1 trace: %c ' + value + ' ', 'color: #054996', 'background: #eee; color: #054996');
    };
    AVM1NativeActions.prototype.unloadMovie = function (target) {
        var nativeTarget = this.context.resolveTarget(target);
        if (!nativeTarget) {
            warning('AVM1Globals.unloadMovie - target not found');
            return; // target was not found
        }
        nativeTarget.unloadMovie();
    };
    AVM1NativeActions.prototype.unloadMovieNum = function (level) {
        level = alToInt32(this.context, level);
        if (level === 0) {
            notImplemented('unloadMovieNum at _level0');
            return;
        }
        this.context.globals._removeRoot(level);
    };
    return AVM1NativeActions;
}());
export { AVM1NativeActions };
